import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isValid, parse } from 'date-fns';
import { es as esLocale } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * TC-UAT / Founder UAT (date UX) — locale-aware date field.
 *  • Direct manual typing (dd/mm/yyyy in ES, mm/dd/yyyy in EN) with strict
 *    validation — impossible dates are rejected, never committed.
 *  • Calendar remains an optional convenience, now with fast year/month
 *    dropdown navigation (no month-by-month clicking to reach a birth year).
 * Value contract unchanged: ISO `yyyy-MM-dd` string in, ISO string out.
 */
export function LocalizedDateInput({
  value, onChange, min, id, className,
  fromYear = 1930, toYear = new Date().getFullYear() + 10,
  'data-testid': testId,
}) {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').toLowerCase().startsWith('es');
  const fmt = isES ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  const placeholder = isES ? 'dd/mm/aaaa' : 'mm/dd/yyyy';

  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  const minDate = min ? new Date(`${min}T00:00:00`) : undefined;

  const [text, setText] = useState(selected && isValid(selected) ? format(selected, fmt) : '');
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const d = value ? new Date(`${value}T00:00:00`) : undefined;
    setText(d && isValid(d) ? format(d, fmt) : '');
    setInvalid(false);
  }, [value, fmt]);

  const tryCommit = (raw) => {
    const s = (raw || '').trim();
    if (!s) { setInvalid(false); onChange(''); return; }
    const normalized = s.replace(/[-.]/g, '/');
    const patterns = isES ? ['dd/MM/yyyy', 'd/M/yyyy'] : ['MM/dd/yyyy', 'M/d/yyyy'];
    let d = null;
    for (const p of patterns) {
      const c = parse(normalized, p, new Date());
      if (isValid(c) && c.getFullYear() >= 1000) { d = c; break; }
    }
    if (!d || (minDate && d < minDate)) { setInvalid(true); return; }
    setInvalid(false);
    onChange(format(d, 'yyyy-MM-dd'));
  };

  const handleType = (raw) => {
    setText(raw);
    const normalized = (raw || '').trim().replace(/[-.]/g, '/');
    if (normalized.length === 10) tryCommit(normalized);
    else setInvalid(false);
  };

  return (
    <div className={cn('flex items-stretch gap-1.5', className)}>
      <div className="flex-1">
        <Input
          id={id}
          inputMode="numeric"
          value={text}
          placeholder={placeholder}
          onChange={(e) => handleType(e.target.value)}
          onBlur={() => tryCommit(text)}
          aria-invalid={invalid || undefined}
          className={cn('h-9', invalid && 'border-destructive focus-visible:ring-destructive')}
          data-testid={testId}
        />
        {invalid && (
          <p className="text-xs text-destructive mt-1" data-testid={testId ? `${testId}-error` : undefined}>
            {isES ? `Fecha inválida — usa ${placeholder}` : `Invalid date — use ${placeholder}`}
          </p>
        )}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-9 px-2.5"
            aria-label={isES ? 'Abrir calendario' : 'Open calendar'}
            data-testid={testId ? `${testId}-calendar-btn` : undefined}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            locale={isES ? esLocale : undefined}
            disabled={minDate ? { before: minDate } : undefined}
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={toYear}
            onSelect={(d) => { if (d) { onChange(format(d, 'yyyy-MM-dd')); setOpen(false); } }}
            initialFocus
          />
          {value ? (
            <div className="p-2 border-t border-border text-right">
              <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => { onChange(''); setOpen(false); }}
                      data-testid={testId ? `${testId}-clear` : undefined}>
                {t('common.clear', { defaultValue: isES ? 'Limpiar' : 'Clear' })}
              </Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default LocalizedDateInput;
